import React, {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '@/app/store/index';
import type { QuoteData, QuoteStep, QuoteValidation } from '../types/quote.types';
import type { Customer } from '@features/customers/types/customers.types';
import type { Dealer } from '@features/dealers/types/dealers.types';
import type {
    Vehicle,
    VehicleDerivative,
    AdditionalVehicleDetailsFormInputs,
} from '@/features/vehiclesearch';
import type { Product } from '@features/products/types/products.types';
import type { PaymentScheduleOptions } from '@features/service-plans/types/payment.types';
import type { PaymentSelection } from '../types/schedule.types';
import { advanceBlockedMessage, firstIncompleteStepBefore, stepAfter } from '../utils/stepGate';
import type { PaymentPreviewBlockReason } from '../utils/stepGate';
import { useStepBlockReason } from '../hooks/useStepBlockReason';
import { useVehicleDetailsUpdate } from '../hooks/useVehicleDetailsUpdate';
import { useBasketPricing } from '../hooks/useBasketPricing';
import { basketPricingInputFrom } from '../utils/pricedLines';
import { isLiveQuote } from '../utils/quoteStatus';
import {
    setCurrentStep,
    setQuoteId,
    setSelectedDealer,
    setSelectedVehicle,
    setSelectedProduct,
    setPaymentOptions,
    setPaymentSelection,
    setSelectedCustomer,
    resetQuote,
    ensureDraftId,
} from '../store/quoteSlice';
import { useQuoteStepOrder } from '../hooks/useQuoteStepOrder';
import { useQuoteSaveLane, type QuoteSaveLaneApi } from '../hooks/useQuoteSaveLane';
import { useQuoteReload, type QuoteReload } from '../hooks/useQuoteReload';
import type { MountLoadMode } from '../utils/mountLoadMode';
import { useAutoSelectSingleDealer } from '../hooks/useAutoSelectSingleDealer';
import {
    useQuoteConfigurationActions,
    type QuoteConfigurationActions,
} from '../hooks/useQuoteConfigurationActions';

interface QuoteContextValue extends QuoteData, QuoteValidation, QuoteConfigurationActions {
    // The resolved order (home-dealer-pinned, P-D1); consumers read it rather than re-resolving.
    stepOrder: readonly QuoteStep[];
    // Navigation
    goToStep: (step: QuoteStep) => void;
    nextStep: () => void;
    previousStep: () => void;

    // Data setters
    // Dealer selection
    selectDealer: (dealer: Dealer | null) => void;

    // Vehicle-specific methods
    selectVehicle: (vehicle: Vehicle | VehicleDerivative | null) => void;
    updateVehicleWithDetails: (data: AdditionalVehicleDetailsFormInputs) => Promise<void>;
    // Issue #825, D3/D4. The form half of the vehicle step gate. Session-scoped:
    // `AdditionalVehicleDetailsForm` reports whether `registrationDate` is
    // currently blocking, and this can only ever ADD a block while that form is
    // mounted — it is never persisted and is cleared unconditionally on
    // unmount and on `reset()`. Exposed as the bare `useState` setter directly:
    // React guarantees a `useState` setter's identity is stable across
    // renders, so no `useCallback` wrapper is needed and the form's effect
    // dependency array does not churn.
    reportVehicleDetailsBlocked: (blocked: boolean) => void;
    // Same pattern, for the payment step: reports WHY the on-screen preview
    // cannot commit, or null when it can (#1643 — a reason, not a boolean).
    reportPaymentPreviewBlocked: (reason: PaymentPreviewBlockReason | null) => void;

    // Product-specific methods
    selectProduct: (product: Product | null) => void;

    // Payment-specific methods
    updatePayment: (payment: PaymentScheduleOptions) => void;
    // #475-B: confirms a scheduling method + schedule-preview snapshot
    updatePaymentSelection: (selection: PaymentSelection) => void;

    // Customer-specific methods
    selectCustomer: (customer: Customer | null) => void;

    // Quote ID setter
    setQuoteId: (quoteId: string) => void;

    // Serialised save lane for this draft
    saveLane: QuoteSaveLaneApi;

    // Server-copy reconcile on mount and the conflict reload
    reload: QuoteReload;

    // Actions
    reset: () => void;
    canProceed: boolean;
    canGoBack: boolean;
}

const QuoteContext = createContext<QuoteContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useQuote = (): QuoteContextValue => {
    const context = useContext(QuoteContext);
    if (context === undefined) {
        throw new Error('useQuote must be used within a QuoteProvider');
    }
    return context;
};

interface QuoteProviderProps {
    children: ReactNode;
    mountLoadMode?: MountLoadMode;
}

export const QuoteProvider: React.FC<QuoteProviderProps> = ({ children, mountLoadMode = 'as-persisted' }) => {
    const dispatch = useDispatch();
    const quoteData = useSelector((state: RootState) => state.quote);
    // Home-dealer-pinned (P-D1) — see useQuoteStepOrder's docblock for why.
    const { order: stepOrder, defer, autoDealer } = useQuoteStepOrder();
    useAutoSelectSingleDealer(autoDealer, defer);
    const saveLane = useQuoteSaveLane();
    const reload = useQuoteReload(mountLoadMode, saveLane);

    useLayoutEffect(() => {
        if (quoteData.draftId === '') {
            dispatch(ensureDraftId());
        }
    }, [dispatch, quoteData.draftId]);

    // P-D4: dispatch the resolved first step on mount, for a fresh journey only.
    // Both guards are needed — quoteId covers a resumed server quote, and
    // completedSteps covers a redux-persist-restored but unsaved journey.
    useEffect(() => {
        const isFreshJourney = !quoteData.quoteId && quoteData.completedSteps.length === 0;
        if (isFreshJourney && quoteData.currentStep !== stepOrder[0]) {
            dispatch(setCurrentStep(stepOrder[0]));
        }
    }, [dispatch, stepOrder, quoteData.quoteId, quoteData.completedSteps.length, quoteData.currentStep]);

    // Issue #825, D4. The form half of the vehicle step gate. Session-scoped
    // component state, deliberately NOT the persisted `quote` Redux slice —
    // persisting "a form that no longer exists said no" would be the stale-flag
    // failure this design exists to avoid. Initial value `false` so a resumed,
    // complete quote is never blocked in the frames before the details form has
    // mounted and reported (D8). Only ever `true` while
    // `AdditionalVehicleDetailsForm` is mounted on the vehicle step: the
    // unmount effect retracts it unconditionally (React always runs cleanup),
    // and `reset()` below clears it explicitly as belt-and-braces.
    const [vehicleDetailsBlocked, setVehicleDetailsBlocked] = useState(false);

    // Mirrors `vehicleDetailsBlocked`: non-null while the on-screen preview cannot
    // commit. Carries WHY (#1643), so the message below can differ per cause.
    const [paymentPreviewBlockReason, setPaymentPreviewBlockReason] =
        useState<PaymentPreviewBlockReason | null>(null);

    // Shares its cache entry with every step calling `useQuoteBasketPricing()` (same key).
    const { selectedProduct, resolvedPlanId, servicePlanSessionId, configuredServices, configuredAddOns } =
        quoteData;
    const pricingInput = useMemo(
        () =>
            basketPricingInputFrom({
                selectedProduct,
                resolvedPlanId,
                servicePlanSessionId,
                configuredServices,
                configuredAddOns,
            }),
        [selectedProduct, resolvedPlanId, servicePlanSessionId, configuredServices, configuredAddOns],
    );
    const pricing = useBasketPricing(pricingInput, !isLiveQuote(quoteData.status));

    const { stepBlockReason, isPricingPending } = useStepBlockReason({
        quoteData,
        stepOrder,
        vehicleDetailsBlocked,
        paymentPreviewBlockReason,
        pricing,
    });

    const isStepValid = (step: QuoteStep): boolean => stepBlockReason(step) === null;

    const getStepErrors = (step: QuoteStep): string[] => {
        return quoteData.errors[step] || [];
    };

    // Issue #931. Was: indexOf + `slice(0, i).every(isStepValid)` inline. Now DEFINED IN TERMS
    // OF `firstIncompleteStepBefore`, for the same anti-drift reason `isStepValid` is defined in
    // terms of `stepBlockReason` above (#825, D6): "can I go?" and "what is stopping me?" must be
    // one computation, or a screen can be told it may proceed while the recovery control it shows
    // disagrees. `firstIncompleteStepBefore`'s `index <= 0` guard also covers `stepOrder.indexOf(step)
    // === -1`, which the old `every` formulation handled differently (`slice(0, -1)` — "every step
    // but the last is valid"). That divergence is unreachable: `QuoteStep` is a closed seven-member
    // union and both step-order constants are total permutations of it, so `indexOf` cannot return
    // -1 for a type-checked argument.
    const canProceedToStep = (step: QuoteStep): boolean =>
        firstIncompleteStepBefore(stepOrder, step, isStepValid) === null;

    const canCompleteQuote = (): boolean => {
        return stepOrder.slice(0, -1).every((s) => isStepValid(s));
    };

    // Navigation
    const goToStep = (step: QuoteStep) => {
        if (canProceedToStep(step)) {
            dispatch(setCurrentStep(step));
        }
    };

    const nextStep = () => {
        // #1738. Next advances to a TARGET, so it is gated on that target's prerequisites — a
        // block arising on an earlier step must not be walkable past, as goToStep already refuses.
        const target = stepAfter(stepOrder, quoteData.currentStep);
        if (target !== null && canProceedToStep(target)) dispatch(setCurrentStep(target));
    };

    const previousStep = () => {
        const currentIndex = stepOrder.indexOf(quoteData.currentStep);
        if (currentIndex > 0) {
            const prevStepName = stepOrder[currentIndex - 1];
            dispatch(setCurrentStep(prevStepName));
        }
    };

    // Data setters
    // Select Dealer
    const selectDealer = (dealer: Dealer | null) => dispatch(setSelectedDealer(dealer));

    // Vehicle Specific Methods
    const selectVehicle = (vehicle: Vehicle | VehicleDerivative | null) =>
        dispatch(setSelectedVehicle(vehicle));

    const updateVehicleWithDetails = useVehicleDetailsUpdate(quoteData.selectedVehicle);

    // Product Specific Methods
    const selectProduct = (product: Product | null) => dispatch(setSelectedProduct(product));

    const configurationActions = useQuoteConfigurationActions({
        configuredServices: quoteData.configuredServices,
        configuredAddOns: quoteData.configuredAddOns,
        stepOrder,
    });

    // Payment Specific Methods
    const updatePayment = (payment: PaymentScheduleOptions) => dispatch(setPaymentOptions(payment));
    const updatePaymentSelection = (selection: PaymentSelection) =>
        dispatch(setPaymentSelection(selection));

    // Select Customer
    const selectCustomer = (customer: Customer | null) => dispatch(setSelectedCustomer(customer));

    const setQuoteIdAction = (quoteId: string) => dispatch(setQuoteId(quoteId));

    const reset = () => {
        dispatch(resetQuote());
        // #825, D4 invariant 4. Belt-and-braces: deselecting the vehicle
        // already unmounts the form (VehicleSelectionStep.tsx), which retracts
        // this via its own unmount effect — but reset() is the one place that
        // discards a whole journey, so the invariant is stated explicitly here
        // too rather than relied on implicitly.
        setVehicleDetailsBlocked(false);
        setPaymentPreviewBlockReason(null);
    };

    // #1738. Defined in terms of the reason (#825, D6), so Next is never enabled while nextStep() refuses.
    const advanceBlockReason = advanceBlockedMessage(stepOrder, quoteData.currentStep, isStepValid, stepBlockReason);
    const canProceed = advanceBlockReason === null;
    const canGoBack = stepOrder.indexOf(quoteData.currentStep) > 0;

    const value: QuoteContextValue = {
        ...quoteData,
        stepOrder,
        isStepValid,
        stepBlockReason,
        isPricingPending,
        getStepErrors,
        canProceedToStep,
        canCompleteQuote,
        advanceBlockReason,
        reportVehicleDetailsBlocked: setVehicleDetailsBlocked,
        reportPaymentPreviewBlocked: setPaymentPreviewBlockReason,
        goToStep,
        nextStep,
        previousStep,
        selectDealer,
        selectVehicle,
        updateVehicleWithDetails,
        selectProduct,
        ...configurationActions,
        updatePayment,
        updatePaymentSelection,
        selectCustomer,
        setQuoteId: setQuoteIdAction,
        saveLane,
        reload,
        reset,
        canProceed,
        canGoBack,
    };

    return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
};
